import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

export const EnquiryForm = ({ vehicleId = null, vehicleTitle = null, compact = false }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || form.name.trim().length < 2) return setError('Please enter your name.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return setError('Please enter a valid email address.');
    if (!form.message.trim() || form.message.trim().length < 5) return setError('Please enter a message (at least 5 characters).');

    setSubmitting(true);
    try {
      await api.post('/enquiries', { ...form, vehicle_id: vehicleId });
      setSuccess(true);
      toast.success('Enquiry sent! Our team will be in touch shortly.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
      toast.error('Could not send enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="enquiry-form-success-message"
        className="flex flex-col items-center text-center py-8 px-4"
      >
        <CheckCircle2 className="h-12 w-12 text-[#24135F] mb-3" />
        <h3 className="font-heading text-2xl tracking-wide text-[#24135F]">ENQUIRY SENT</h3>
        <p className="text-sm text-[#53565A] mt-2 max-w-xs">
          Thanks {form.name.split(' ')[0]}! Our team will be in touch with you shortly
          {vehicleTitle ? ` about the ${vehicleTitle}` : ''}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {/* Honeypot - hidden from humans, bots fill it and get silently dropped */}
      <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="enq-website">Website</label>
        <input
          id="enq-website"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={set('website')}
        />
      </div>
      <div>
        <Label htmlFor="enq-name" className="text-xs font-semibold text-[#53565A]">
          Your Name <span className="text-[#E4002B]">*</span>
        </Label>
        <Input
          id="enq-name"
          data-testid="enquiry-form-name-input"
          value={form.name}
          onChange={set('name')}
          placeholder="John Smith"
          className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
        />
      </div>
      <div>
        <Label htmlFor="enq-email" className="text-xs font-semibold text-[#53565A]">
          Your Email <span className="text-[#E4002B]">*</span>
        </Label>
        <Input
          id="enq-email"
          type="email"
          data-testid="enquiry-form-email-input"
          value={form.email}
          onChange={set('email')}
          placeholder="john@example.com"
          className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
        />
      </div>
      <div>
        <Label htmlFor="enq-phone" className="text-xs font-semibold text-[#53565A]">
          Phone Number
        </Label>
        <Input
          id="enq-phone"
          type="tel"
          data-testid="enquiry-form-phone-input"
          value={form.phone}
          onChange={set('phone')}
          placeholder="0400 000 000"
          className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
        />
      </div>
      <div>
        <Label htmlFor="enq-message" className="text-xs font-semibold text-[#53565A]">
          Your Message <span className="text-[#E4002B]">*</span>
        </Label>
        <Textarea
          id="enq-message"
          data-testid="enquiry-form-message-textarea"
          value={form.message}
          onChange={set('message')}
          rows={compact ? 4 : 5}
          placeholder={vehicleTitle ? `I'm interested in the ${vehicleTitle}...` : 'How can we help?'}
          className="mt-1.5 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
        />
      </div>
      {error && (
        <p data-testid="enquiry-form-error-message" role="alert" className="text-sm text-[#E4002B] font-medium">
          {error}
        </p>
      )}
      <Button
        type="submit"
        disabled={submitting}
        data-testid="enquiry-form-submit-button"
        className="w-full rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-12 font-bold tracking-wider transition-colors duration-150"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        {submitting ? 'SENDING...' : vehicleId ? 'SEND ENQUIRY' : 'SEND MESSAGE'}
      </Button>
    </form>
  );
};
